/**
 * Persists learner resume positions for "Keep learning" — one slot per quick-action area.
 */
import type { AppLocale } from '../i18n';
import { pickLocalized } from './localizedContent';
const LEGACY_STORAGE_KEY = 'learnerLastLearningContext';
const RESUME_SLOTS_KEY = 'learnerLearningResumeSlots';
const MAX_SLOTS = 12;

export type LastLearningContext =
  | { kind: 'lesson'; lessonId: number; updatedAt: number }
  | { kind: 'tag'; tagId: number; blockId?: number; lessonId?: number; updatedAt: number };

export type LearningResumeKind = 'lesson' | 'tag' | 'quiz' | 'exercise' | 'courses' | 'book';

export type LearningResumePayload =
  | {
      kind: 'lesson';
      lessonId: number;
      courseId: number;
      levelId?: number;
      courseTitle?: string;
      courseTitleFa?: string;
      levelTitle?: string;
      levelTitleFa?: string;
      lessonTitle?: string;
      lessonTitleFa?: string;
      isDesignedForNonFarsiUsers?: boolean;
    }
  | {
      kind: 'tag';
      tagId: number;
      blockId?: number;
      lessonId?: number;
      browseMode?: 'blocks' | 'lessons';
      lessonIsDesignedForNonFarsiUsers?: boolean;
      itemTitleFa?: string;
    }
  | {
      kind: 'quiz';
      flowKind?: string;
      quizId?: number;
      itemTitle?: string;
      itemTitleFa?: string;
      progressDone?: number;
      progressTotal?: number;
    }
  | {
      kind: 'exercise';
      flowKind?: string;
      exerciseId?: number;
      itemTitle?: string;
      itemTitleFa?: string;
      progressDone?: number;
      progressTotal?: number;
    }
  | { kind: 'courses'; courseId?: number; levelId?: number; lessonId?: number }
  | {
      kind: 'book';
      bookId: number;
      pageIndex: number;
      bookTitle?: string;
      bookTitleFa?: string;
      pageCount?: number;
    };

export interface LearningResumeEntry {
  id: string;
  kind: LearningResumeKind;
  title: string;
  titleFa?: string;
  subtitle?: string;
  subtitleFa?: string;
  /** Lucide icon name — tag quick-action card icon. */
  icon?: string;
  /** Tag card accent color (hex). */
  accentColor?: string;
  backgroundImageUrl?: string | null;
  updatedAt: number;
  payload: LearningResumePayload;
}

function readResumeSlots(): LearningResumeEntry[] {
  try {
    const raw = localStorage.getItem(RESUME_SLOTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidResumeEntry);
  } catch {
    return [];
  }
}

function writeResumeSlots(entries: LearningResumeEntry[]): void {
  try {
    const sorted = [...entries].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, MAX_SLOTS);
    localStorage.setItem(RESUME_SLOTS_KEY, JSON.stringify(sorted));
  } catch {
    /* ignore quota / private mode */
  }
}

function isValidResumeEntry(value: unknown): value is LearningResumeEntry {
  if (!value || typeof value !== 'object') return false;
  const e = value as Partial<LearningResumeEntry>;
  if (typeof e.id !== 'string' || typeof e.kind !== 'string' || typeof e.title !== 'string') return false;
  if (typeof e.updatedAt !== 'number' || !e.payload || typeof e.payload !== 'object') return false;
  const kind = e.kind as LearningResumeKind;
  if (!['lesson', 'tag', 'quiz', 'exercise', 'courses', 'book'].includes(kind)) return false;
  if ((e.payload as { kind?: string }).kind !== kind) return false;
  if (kind === 'lesson') {
    const p = e.payload as Extract<LearningResumePayload, { kind: 'lesson' }>;
    if (typeof p.lessonId !== 'number') return false;
    // Legacy single-slot entries lack courseId — still valid for resume.
    return true;
  }
  if (kind === 'book') {
    const p = e.payload as Extract<LearningResumePayload, { kind: 'book' }>;
    return typeof p.bookId === 'number' && typeof p.pageIndex === 'number';
  }
  return true;
}

export function upsertLearningResumeEntry(
  entry: Omit<LearningResumeEntry, 'updatedAt'> & { updatedAt?: number }
): void {
  const updatedAt = entry.updatedAt ?? Date.now();
  const next: LearningResumeEntry = { ...entry, updatedAt };
  let slots = readResumeSlots().filter((s) => s.id !== entry.id);
  if (next.kind === 'lesson') {
    slots = pruneStaleLessonSlots(slots, next);
  }
  slots.push(next);
  writeResumeSlots(dedupeLessonResumeEntries(slots));

  // Keep legacy single-context key in sync with the most recent slot.
  try {
    const legacy = resumeEntryToLegacyContext(next);
    if (legacy) localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(legacy));
  } catch {
    /* ignore */
  }
}

function resumeEntryToLegacyContext(entry: LearningResumeEntry): LastLearningContext | null {
  if (entry.payload.kind === 'lesson') {
    return { kind: 'lesson', lessonId: entry.payload.lessonId, updatedAt: entry.updatedAt };
  }
  if (entry.payload.kind === 'tag') {
    return {
      kind: 'tag',
      ...normalizeTagPayload(entry.payload),
      updatedAt: entry.updatedAt
    };
  }
  return null;
}

function normalizeTagPayload(
  payload: Extract<LearningResumePayload, { kind: 'tag' }>
): Extract<LearningResumePayload, { kind: 'tag' }> {
  if (payload.lessonId != null) {
    return {
      kind: 'tag',
      tagId: payload.tagId,
      lessonId: payload.lessonId,
      browseMode: payload.browseMode ?? 'lessons'
    };
  }
  if (payload.blockId != null) {
    return {
      kind: 'tag',
      tagId: payload.tagId,
      blockId: payload.blockId,
      browseMode: payload.browseMode ?? 'blocks'
    };
  }
  return { kind: 'tag', tagId: payload.tagId };
}

function normalizeResumeEntry(entry: LearningResumeEntry): LearningResumeEntry {
  if (entry.payload.kind !== 'tag') return entry;
  return { ...entry, payload: normalizeTagPayload(entry.payload) };
}

function lessonResumeEntryScore(entry: LearningResumeEntry): number {
  if (entry.kind !== 'lesson' || entry.payload.kind !== 'lesson') return 0;
  let score = 0;
  if (entry.payload.courseTitle?.trim()) score += 4;
  if (entry.payload.levelTitle?.trim()) score += 2;
  if (entry.id.startsWith('lesson:course:')) score += 8;
  if (entry.id !== 'lesson') score += 1;
  return score;
}

/** Collapse legacy `lesson` slots and duplicate lessonId rows — keep the richest per lesson. */
function dedupeLessonResumeEntries(entries: LearningResumeEntry[]): LearningResumeEntry[] {
  const nonLessons: LearningResumeEntry[] = [];
  const bestByLessonId = new Map<number, LearningResumeEntry>();

  for (const entry of entries) {
    if (entry.kind !== 'lesson' || entry.payload.kind !== 'lesson') {
      nonLessons.push(entry);
      continue;
    }

    const lessonId = entry.payload.lessonId;
    const existing = bestByLessonId.get(lessonId);
    if (!existing) {
      bestByLessonId.set(lessonId, entry);
      continue;
    }

    const entryScore = lessonResumeEntryScore(entry);
    const existingScore = lessonResumeEntryScore(existing);
    if (entryScore > existingScore || (entryScore === existingScore && entry.updatedAt > existing.updatedAt)) {
      bestByLessonId.set(lessonId, entry);
    }
  }

  return [...nonLessons, ...bestByLessonId.values()];
}

function pruneStaleLessonSlots(slots: LearningResumeEntry[], next: LearningResumeEntry): LearningResumeEntry[] {
  if (next.kind !== 'lesson' || next.payload.kind !== 'lesson') return slots;

  const { lessonId, courseId } = next.payload;
  return slots.filter((slot) => {
    if (slot.id === next.id) return false;
    if (slot.kind !== 'lesson' || slot.payload.kind !== 'lesson') return true;
    if (slot.id === 'lesson') return false;
    if (slot.payload.lessonId === lessonId) return false;
    if (courseId > 0 && slot.payload.courseId === courseId) return false;
    return true;
  });
}

function normalizeStoredResumeSlots(): LearningResumeEntry[] {
  const normalized = dedupeLessonResumeEntries(readResumeSlots().map(normalizeResumeEntry));
  writeResumeSlots(normalized);
  return normalized.sort((a, b) => b.updatedAt - a.updatedAt);
}

export function getLearningResumeEntries(): LearningResumeEntry[] {
  return normalizeStoredResumeSlots();
}

/** Entries shown in the Keep Learning choice modal — lessons/tags/books/quiz/exercise only. */
export function getKeepLearningModalEntries(): LearningResumeEntry[] {
  return getLearningResumeEntries().filter((e) => e.kind !== 'courses');
}

export function formatStructuredLessonPath(
  payload: Extract<LearningResumePayload, { kind: 'lesson' }>,
  lessonTitle?: string,
  locale: AppLocale = 'en'
): string {
  const parts = [
    pickLocalized(payload.courseTitle, payload.courseTitleFa, locale),
    pickLocalized(payload.levelTitle, payload.levelTitleFa, locale),
    lessonTitle?.trim() || pickLocalized(payload.lessonTitle, payload.lessonTitleFa, locale) || undefined
  ].filter((p): p is string => Boolean(p?.trim()));
  return parts.join(' › ');
}

export function saveLastLessonPosition(
  lessonId: number,
  meta?: {
    lessonTitle?: string;
    lessonTitleFa?: string;
    courseId?: number;
    levelId?: number;
    courseTitle?: string;
    courseTitleFa?: string;
    levelTitle?: string;
    levelTitleFa?: string;
    isDesignedForNonFarsiUsers?: boolean;
  }
): void {
  const courseId = meta?.courseId;
  const lessonTitleEn = meta?.lessonTitle?.trim() || 'Lesson';
  const lessonTitleFa = meta?.lessonTitleFa?.trim();
  const path = formatStructuredLessonPath(
    {
      kind: 'lesson',
      lessonId,
      courseId: courseId ?? 0,
      levelId: meta?.levelId,
      courseTitle: meta?.courseTitle,
      courseTitleFa: meta?.courseTitleFa,
      levelTitle: meta?.levelTitle,
      levelTitleFa: meta?.levelTitleFa,
      lessonTitle: lessonTitleEn,
      lessonTitleFa
    },
    lessonTitleEn
  );

  if (courseId == null || courseId <= 0) return;

  upsertLearningResumeEntry({
    id: `lesson:course:${courseId}`,
    kind: 'lesson',
    title: lessonTitleEn,
    titleFa: lessonTitleFa,
    subtitle: path || meta?.levelTitle?.trim() || undefined,
    payload: {
      kind: 'lesson',
      lessonId,
      courseId,
      levelId: meta?.levelId,
      courseTitle: meta?.courseTitle?.trim() || undefined,
      courseTitleFa: meta?.courseTitleFa?.trim() || undefined,
      levelTitle: meta?.levelTitle?.trim() || undefined,
      levelTitleFa: meta?.levelTitleFa?.trim() || undefined,
      lessonTitle: lessonTitleEn,
      lessonTitleFa,
      isDesignedForNonFarsiUsers: meta?.isDesignedForNonFarsiUsers
    }
  });
}

export function saveLastBookPosition(
  bookId: number,
  pageIndex: number,
  meta?: { bookTitle?: string; bookTitleFa?: string; pageCount?: number }
): void {
  const title = meta?.bookTitle?.trim() || 'Library book';
  const titleFa = meta?.bookTitleFa?.trim();

  upsertLearningResumeEntry({
    id: `book:${bookId}`,
    kind: 'book',
    title,
    titleFa,
    payload: {
      kind: 'book',
      bookId,
      pageIndex: Math.max(0, pageIndex),
      bookTitle: title,
      bookTitleFa: titleFa,
      pageCount: meta?.pageCount
    }
  });
}

export function saveLastTagPosition(
  tagId: number,
  blockId?: number,
  meta?: {
    tagTitle?: string;
    tagTitleFa?: string;
    itemTitle?: string;
    itemTitleFa?: string;
    lessonId?: number;
    tagIcon?: string;
    tagColor?: string;
    tagBackgroundImageUrl?: string | null;
    browseMode?: 'blocks' | 'lessons';
    lessonIsDesignedForNonFarsiUsers?: boolean;
  }
): void {
  const tagPayload: Extract<LearningResumePayload, { kind: 'tag' }> = { kind: 'tag', tagId };
  if (meta?.lessonId != null) {
    tagPayload.lessonId = meta.lessonId;
    tagPayload.browseMode = meta.browseMode ?? 'lessons';
    if (meta.lessonIsDesignedForNonFarsiUsers != null) {
      tagPayload.lessonIsDesignedForNonFarsiUsers = meta.lessonIsDesignedForNonFarsiUsers;
    }
  } else if (blockId != null) {
    tagPayload.blockId = blockId;
    tagPayload.browseMode = meta?.browseMode ?? 'blocks';
  }

  upsertLearningResumeEntry({
    id: `tag:${tagId}`,
    kind: 'tag',
    title: meta?.tagTitle?.trim() || 'Tagged content',
    titleFa: meta?.tagTitleFa?.trim(),
    subtitle: meta?.itemTitle?.trim() || undefined,
    subtitleFa: meta?.itemTitleFa?.trim(),
    icon: meta?.tagIcon?.trim() || undefined,
    accentColor: meta?.tagColor?.trim() || undefined,
    backgroundImageUrl: meta?.tagBackgroundImageUrl ?? undefined,
    payload: {
      ...tagPayload,
      itemTitleFa: meta?.itemTitleFa?.trim()
    }
  });
}

export type QuizFlowResumeItem = {
  flowKind: string;
  quizId: number;
  itemTitle?: string;
  itemTitleFa?: string;
};

export type ExerciseFlowResumeItem = {
  flowKind: string;
  exerciseId: number;
  itemTitle?: string;
  itemTitleFa?: string;
};

function mergeQuizResumePayload(
  item: QuizFlowResumeItem | undefined,
  existing: LearningResumeEntry | undefined,
  progress?: { done: number; total: number }
): Extract<LearningResumePayload, { kind: 'quiz' }> {
  const prev = existing?.payload;
  const baseProgress =
    progress ??
    (prev?.kind === 'quiz'
      ? { done: prev.progressDone ?? 0, total: prev.progressTotal ?? 0 }
      : undefined);

  if (item) {
    return {
      kind: 'quiz',
      flowKind: item.flowKind,
      quizId: item.quizId,
      itemTitle: item.itemTitle?.trim() || undefined,
      itemTitleFa: item.itemTitleFa?.trim() || undefined,
      progressDone: baseProgress?.done,
      progressTotal: baseProgress?.total
    };
  }
  if (prev?.kind === 'quiz' && prev.quizId != null && prev.flowKind) {
    return {
      kind: 'quiz',
      flowKind: prev.flowKind,
      quizId: prev.quizId,
      itemTitle: prev.itemTitle,
      itemTitleFa: prev.itemTitleFa,
      progressDone: baseProgress?.done ?? prev.progressDone,
      progressTotal: baseProgress?.total ?? prev.progressTotal
    };
  }
  return {
    kind: 'quiz',
    progressDone: baseProgress?.done,
    progressTotal: baseProgress?.total
  };
}

function mergeExerciseResumePayload(
  item: ExerciseFlowResumeItem | undefined,
  existing: LearningResumeEntry | undefined,
  progress?: { done: number; total: number }
): Extract<LearningResumePayload, { kind: 'exercise' }> {
  const prev = existing?.payload;
  const baseProgress =
    progress ??
    (prev?.kind === 'exercise'
      ? { done: prev.progressDone ?? 0, total: prev.progressTotal ?? 0 }
      : undefined);

  if (item) {
    return {
      kind: 'exercise',
      flowKind: item.flowKind,
      exerciseId: item.exerciseId,
      itemTitle: item.itemTitle?.trim() || undefined,
      itemTitleFa: item.itemTitleFa?.trim() || undefined,
      progressDone: baseProgress?.done,
      progressTotal: baseProgress?.total
    };
  }
  if (prev?.kind === 'exercise' && prev.exerciseId != null && prev.flowKind) {
    return {
      kind: 'exercise',
      flowKind: prev.flowKind,
      exerciseId: prev.exerciseId,
      itemTitle: prev.itemTitle,
      itemTitleFa: prev.itemTitleFa,
      progressDone: baseProgress?.done ?? prev.progressDone,
      progressTotal: baseProgress?.total ?? prev.progressTotal
    };
  }
  return {
    kind: 'exercise',
    progressDone: baseProgress?.done,
    progressTotal: baseProgress?.total
  };
}

export function saveQuizFlowPosition(
  title?: string,
  subtitle?: string,
  item?: QuizFlowResumeItem,
  options?: { touchUpdatedAt?: boolean; progress?: { done: number; total: number } }
): void {
  const existing = readResumeSlots().find((s) => s.id === 'quiz');
  const payload = mergeQuizResumePayload(item, existing, options?.progress);
  const itemTitleEn = item?.itemTitle?.trim() || payload.itemTitle?.trim();
  const itemTitleFa = item?.itemTitleFa?.trim() || payload.itemTitleFa?.trim();
  const touchUpdatedAt = options?.touchUpdatedAt ?? Boolean(item);

  upsertLearningResumeEntry({
    id: 'quiz',
    kind: 'quiz',
    title: itemTitleEn || title?.trim() || 'Quiz run',
    titleFa: itemTitleFa,
    subtitle: subtitle?.trim() || existing?.subtitle || undefined,
    updatedAt: touchUpdatedAt ? Date.now() : existing?.updatedAt,
    payload
  });
}

export function saveExerciseFlowPosition(
  title?: string,
  subtitle?: string,
  item?: ExerciseFlowResumeItem,
  options?: { touchUpdatedAt?: boolean; progress?: { done: number; total: number } }
): void {
  const existing = readResumeSlots().find((s) => s.id === 'exercise');
  const payload = mergeExerciseResumePayload(item, existing, options?.progress);
  const itemTitleEn = item?.itemTitle?.trim() || payload.itemTitle?.trim();
  const itemTitleFa = item?.itemTitleFa?.trim() || payload.itemTitleFa?.trim();
  const touchUpdatedAt = options?.touchUpdatedAt ?? Boolean(item);

  upsertLearningResumeEntry({
    id: 'exercise',
    kind: 'exercise',
    title: itemTitleEn || title?.trim() || 'Practice exercises',
    titleFa: itemTitleFa,
    subtitle: subtitle?.trim() || existing?.subtitle || undefined,
    updatedAt: touchUpdatedAt ? Date.now() : existing?.updatedAt,
    payload
  });
}

export function quizFlowItemElementId(flowKind: string, quizId: number): string {
  return `quiz-flow-item-${flowKind}-${quizId}`;
}

export function exerciseFlowItemElementId(flowKind: string, exerciseId: number): string {
  return `exercise-flow-item-${flowKind}-${exerciseId}`;
}

export function saveCoursesPosition(meta?: {
  title?: string;
  subtitle?: string;
  courseId?: number;
  levelId?: number;
  lessonId?: number;
}): void {
  upsertLearningResumeEntry({
    id: 'courses',
    kind: 'courses',
    title: meta?.title?.trim() || 'Courses',
    subtitle: meta?.subtitle?.trim() || undefined,
    payload: {
      kind: 'courses',
      courseId: meta?.courseId,
      levelId: meta?.levelId,
      lessonId: meta?.lessonId
    }
  });
}

export function getLastLearningContext(): LastLearningContext | null {
  const entries = getLearningResumeEntries();
  for (const entry of entries) {
    const legacy = resumeEntryToLegacyContext(entry);
    if (legacy) return legacy;
  }

  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<LastLearningContext> & { kind?: string };
    if (p?.kind === 'lesson' && typeof (p as { lessonId?: number }).lessonId === 'number') {
      return { kind: 'lesson', lessonId: (p as { lessonId: number }).lessonId, updatedAt: (p as { updatedAt?: number }).updatedAt ?? 0 };
    }
    if (p?.kind === 'tag' && typeof (p as { tagId?: number }).tagId === 'number') {
      return {
        kind: 'tag',
        tagId: (p as { tagId: number }).tagId,
        blockId: typeof (p as { blockId?: number }).blockId === 'number' ? (p as { blockId: number }).blockId : undefined,
        lessonId: typeof (p as { lessonId?: number }).lessonId === 'number' ? (p as { lessonId: number }).lessonId : undefined,
        updatedAt: (p as { updatedAt?: number }).updatedAt ?? 0
      };
    }
    return null;
  } catch {
    return null;
  }
}
